import React from 'react';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import { Stack } from '@mui/system';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import NoteIcon from '@mui/icons-material/Note';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/Save';
import IconButtonMenu from '../Redesing/icon-button-menu';
import CardView from '../Redesing/card-view';
import {
  getLocaleDateTimeString,
  getLocaleDateString,
  getPriorityName,
} from '../../utils';
import { ITicket } from '../../ts/interface/problem.interface';
import { IAdmin } from '../../ts/interface/admins.interface';

interface TimelineEvent {
  type: 'created' | 'note' | 'state_change' | 'slack_review' | 'closed' | 'canceled';
  timestamp: string;
  content: React.ReactNode;
  color: 'primary' | 'grey' | 'info' | 'warning' | 'success' | 'error';
  icon: React.ReactNode;
}

interface TicketTimelineProps {
  ticket: ITicket;
  communicationLog: readonly unknown[] | null;
  resolutionData: {
    reason?: string;
    details?: string;
    resolved_by?: string;
  } | null;
  admins: IAdmin[];
  note: string;
  onNoteChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSaveNote: () => void;
  getResolutionReasonLabel: (reason: string) => string;
  isLoading: boolean;
}

function resolveAdminName(email: string | null | undefined, admins: IAdmin[]): string {
  if (!email) return 'Unassigned';
  const admin = admins.find((a) => a.email === email);
  return admin ? `${admin.firstName} ${admin.lastName}` : email;
}

function buildStateChangeMessage(
  entry: Record<string, unknown>,
  admins: IAdmin[]
): string {
  const field = entry.field as string;
  const oldVal = entry.old_value;
  const newVal = entry.new_value;

  switch (field) {
    case 'assignee':
      return `Assignee changed from ${resolveAdminName(oldVal as string, admins)} to ${resolveAdminName(newVal as string, admins)}`;
    case 'priority':
      return `Priority changed from ${getPriorityName(oldVal as number)} to ${getPriorityName(newVal as number)}`;
    case 'due_on':
      return `Target Date changed from ${getLocaleDateString(oldVal as string)} to ${getLocaleDateString(newVal as string)}`;
    default:
      return `${field} changed from ${oldVal} to ${newVal}`;
  }
}

function buildEvents(
  ticket: ITicket,
  communicationLog: readonly unknown[] | null,
  resolutionData: TicketTimelineProps['resolutionData'],
  admins: IAdmin[],
  getResolutionReasonLabel: (reason: string) => string
): TimelineEvent[] {
  const events: TimelineEvent[] = [];

  // Created event
  if (ticket.created_on) {
    events.push({
      type: 'created',
      timestamp: ticket.created_on as string,
      color: 'primary',
      icon: <AddCircleOutlineIcon />,
      content: (
        <Box>
          <Typography variant="subtitle2">Ticket Created</Typography>
          {ticket.reporter_name && (
            <Typography variant="body2" color="text.secondary">
              by {ticket.reporter_name as string}
            </Typography>
          )}
          {ticket.description && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {(ticket.description as string).split('\n').map((line, i) => (
                <React.Fragment key={i}>
                  {line}
                  {i < (ticket.description as string).split('\n').length - 1 && <br />}
                </React.Fragment>
              ))}
            </Typography>
          )}
        </Box>
      ),
    });
  }

  // Communication log entries
  if (communicationLog && Array.isArray(communicationLog)) {
    for (const raw of communicationLog) {
      let entry: Record<string, unknown>;
      if (typeof raw === 'string') {
        try {
          entry = JSON.parse(raw);
        } catch {
          continue;
        }
      } else {
        entry = raw as Record<string, unknown>;
      }

      if (entry.type === 'state_change') {
        events.push({
          type: 'state_change',
          timestamp: entry.created_on as string,
          color: 'info',
          icon: <SwapHorizIcon />,
          content: (
            <Box>
              <Typography variant="subtitle2">
                {buildStateChangeMessage(entry, admins)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                by {resolveAdminName(entry.admin_id as string, admins)}
              </Typography>
            </Box>
          ),
        });
      } else if (entry.source === 'slack_early_end_review') {
        events.push({
          type: 'slack_review',
          timestamp: entry.created_on as string,
          color: 'warning',
          icon: <WarningAmberIcon />,
          content: (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle2">Slack Review</Typography>
                <Chip label={entry.action as string} size="small" color="primary" />
                {entry.is_safety_issue && (
                  <Chip
                    icon={<WarningAmberIcon />}
                    label="Safety Issue"
                    size="small"
                    color="warning"
                  />
                )}
              </Box>
              {entry.safety_reason && (
                <Typography variant="body2">
                  <strong>Safety Concern:</strong> {entry.safety_reason as string}
                </Typography>
              )}
            </Box>
          ),
        });
      } else if (entry.message) {
        // Regular note
        events.push({
          type: 'note',
          timestamp: entry.created_on as string,
          color: 'grey',
          icon: <NoteIcon />,
          content: (
            <Box>
              <Typography variant="subtitle2">
                {resolveAdminName(
                  (entry.admin_id || entry.user_id) as string,
                  admins
                )}
                {entry.type === 'internal' && (
                  <Chip label="Internal" size="small" sx={{ ml: 1 }} />
                )}
              </Typography>
              <Typography variant="body2">{entry.message as string}</Typography>
            </Box>
          ),
        });
      }
    }
  }

  // Closed event
  if (ticket.completed_on) {
    events.push({
      type: 'closed',
      timestamp: ticket.completed_on as string,
      color: 'success',
      icon: <CheckCircleIcon />,
      content: (
        <Box>
          <Typography variant="subtitle2">Ticket Closed</Typography>
          {resolutionData?.resolved_by && (
            <Typography variant="body2" color="text.secondary">
              by {resolveAdminName(resolutionData.resolved_by, admins)}
            </Typography>
          )}
          {resolutionData?.reason && (
            <Typography variant="body2">
              Reason: {getResolutionReasonLabel(resolutionData.reason)}
            </Typography>
          )}
          {resolutionData?.details && (
            <Typography variant="body2">{resolutionData.details}</Typography>
          )}
        </Box>
      ),
    });
  }

  // Canceled event
  if (ticket.canceled_on) {
    events.push({
      type: 'canceled',
      timestamp: ticket.canceled_on as string,
      color: 'error',
      icon: <CancelIcon />,
      content: (
        <Box>
          <Typography variant="subtitle2">Ticket Canceled</Typography>
          {resolutionData?.resolved_by && (
            <Typography variant="body2" color="text.secondary">
              by {resolveAdminName(resolutionData.resolved_by, admins)}
            </Typography>
          )}
          {resolutionData?.reason && (
            <Typography variant="body2">
              Reason: {getResolutionReasonLabel(resolutionData.reason)}
            </Typography>
          )}
          {resolutionData?.details && (
            <Typography variant="body2">{resolutionData.details}</Typography>
          )}
        </Box>
      ),
    });
  }

  // Sort chronologically
  events.sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  return events;
}

const TicketTimeline: React.FC<TicketTimelineProps> = ({
  ticket,
  communicationLog,
  resolutionData,
  admins,
  note,
  onNoteChange,
  onSaveNote,
  getResolutionReasonLabel,
  isLoading,
}) => {
  const events = buildEvents(
    ticket,
    communicationLog,
    resolutionData,
    admins,
    getResolutionReasonLabel
  );

  return (
    <CardView
      isLoading={isLoading}
      title="Timeline"
      content={
        <Box>
          <Timeline
            sx={{
              '& .MuiTimelineOppositeContent-root': {
                flex: 0.2,
                minWidth: 100,
              },
              px: 0,
            }}
          >
            {events.map((event, index) => (
              <TimelineItem key={index}>
                <TimelineOppositeContent color="text.secondary" variant="body2">
                  {getLocaleDateTimeString(event.timestamp)}
                </TimelineOppositeContent>
                <TimelineSeparator>
                  <TimelineDot color={event.color}>{event.icon}</TimelineDot>
                  {index < events.length - 1 && <TimelineConnector />}
                </TimelineSeparator>
                <TimelineContent>{event.content}</TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>

          <Stack px={2} pb={2} spacing={2}>
            <TextField
              maxRows={10}
              minRows={2}
              multiline
              variant="outlined"
              fullWidth
              value={note}
              onChange={onNoteChange}
              placeholder="Add note"
            />
            <IconButtonMenu
              buttons={[
                {
                  label: 'Save Note',
                  onClick: onSaveNote,
                  disabled: !note,
                  startIcon: <SaveIcon />,
                },
              ]}
            />
          </Stack>
        </Box>
      }
    />
  );
};

export default TicketTimeline;
